import React, { FC, useEffect, useState } from "react"
import { Form, Select, Modal, Button, Space } from "antd"
import { observer } from "mobx-react"
import { notifications } from "notifications"
import { useStore } from "stores/utils/use-store"
import { CountryGroupDto } from "../store/types"
import { CountryTagList } from "./shared/CountryTagList"
import { getAvailableCountryOptions, getCountryOptionsFromCodes, defaultFilterOption } from "../utils"

interface ManageCountriesModalProps {
  visible: boolean
  onClose: () => void
  countryGroup: CountryGroupDto
}

export const ManageCountriesModal: FC<ManageCountriesModalProps> = observer(({
  visible,
  onClose,
  countryGroup: initialCountryGroup,
}) => {
  const { dataStore: { countryGroupsStore } } = useStore()
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const [actionType, setActionType] = useState<"add" | "remove" | null>(null)

  const countryGroup = countryGroupsStore.selectedGroup || initialCountryGroup

  const availableToAdd = getAvailableCountryOptions(countryGroup.countries)
  const countriesToRemoveOptions = getCountryOptionsFromCodes(countryGroup.countries)

  useEffect(() => {
    if (visible && initialCountryGroup) {
      countryGroupsStore.getCountryGroupById(initialCountryGroup.id)
      form.resetFields()
      setActionType(null)
    }
  }, [visible, initialCountryGroup, form, countryGroupsStore])

  const handleAddCountries = async() => {
    const values = await form.validateFields(["countriesToAdd"])
    if (!values.countriesToAdd || values.countriesToAdd.length === 0) {
      notifications.error("Please select at least one country to add")

      return
    }

    setSubmitting(true)
    setActionType("add")
    try {
      await countryGroupsStore.addCountriesToGroup(countryGroup.id, { countries: values.countriesToAdd })
      notifications.success("Countries added successfully")
      setSubmitting(false)
      setActionType(null)
      form.resetFields(["countriesToAdd"])
      await countryGroupsStore.getCountryGroupById(countryGroup.id)
    } catch (error: any) {
      notifications.error(error?.cause?.message || error?.message || "Failed to add countries")
      setSubmitting(false)
      setActionType(null)
    }
  }

  const handleRemoveCountries = async() => {
    const values = await form.validateFields(["countriesToRemove"])
    if (!values.countriesToRemove || values.countriesToRemove.length === 0) {
      notifications.error("Please select at least one country to remove")

      return
    }

    setSubmitting(true)
    setActionType("remove")
    try {
      await countryGroupsStore.removeCountriesFromGroup(countryGroup.id, { countries: values.countriesToRemove })
      notifications.success("Countries removed successfully")
      setSubmitting(false)
      setActionType(null)
      form.resetFields(["countriesToRemove"])
      await countryGroupsStore.getCountryGroupById(countryGroup.id)
    } catch (error: any) {
      notifications.error(error?.cause?.message || error?.message || "Failed to remove countries")
      setSubmitting(false)
      setActionType(null)
    }
  }

  return (
    <Modal
      open={visible}
      title={`Manage Countries - ${countryGroup.name}`}
      width="700px"
      footer={null}
      onCancel={onClose}
      style={{ top: "10px" }}
    >
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8 }}>
          <strong>Current Countries:</strong>
        </div>
        <CountryTagList countries={countryGroup.countries} />
      </div>

      <Form form={form} layout="vertical">
        <Form.Item
          label="Add Countries"
          name="countriesToAdd"
        >
          <Select
            mode="multiple"
            placeholder="Select countries to add"
            allowClear
            showSearch
            filterOption={defaultFilterOption}
            style={{ width: "100%" }}
            options={availableToAdd}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            onClick={handleAddCountries}
            loading={submitting && actionType === "add"}
            disabled={submitting}
            style={{ marginRight: 8 }}
          >
            Add Countries
          </Button>
        </Form.Item>

        <Form.Item
          label="Remove Countries"
          name="countriesToRemove"
        >
          <Select
            mode="multiple"
            placeholder="Select countries to remove"
            allowClear
            showSearch
            filterOption={defaultFilterOption}
            style={{ width: "100%" }}
            options={countriesToRemoveOptions}
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              danger
              onClick={handleRemoveCountries}
              loading={submitting && actionType === "remove"}
              disabled={submitting}
            >
              Remove Countries
            </Button>
            <Button onClick={onClose}>
              Close
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  )
})
